import { AppConfig } from './socket';
import { AppState, localStorageManager, STORAGE_KEYS } from './localStorage';

// Claves excluidas del snapshot para evitar recursión (backups dentro de backups),
// conflictos de master election al importar, o ensuciar colas de eventos inter-ventana.
const SNAPSHOT_EXCLUDED_PREFIXES = [
  STORAGE_KEYS.BACKUP_PREFIX,
  STORAGE_KEYS.EVENT_PREFIX
];
const SNAPSHOT_EXCLUDED_KEYS = new Set<string>([
  STORAGE_KEYS.TIMER_MASTER,
  STORAGE_KEYS.TIMER_CONTROL
]);
const APP_KEY_PREFIX = 'neurolimpiadas_';

const isExcludedFromSnapshot = (key: string): boolean => {
  if (SNAPSHOT_EXCLUDED_KEYS.has(key)) return true;
  return SNAPSHOT_EXCLUDED_PREFIXES.some(prefix => key.startsWith(prefix));
};

// Interfaces para la estructura del snapshot
export interface SnapshotMetadata {
  version: string;
  exportDate: string;
  exportTimestamp: number;
  appVersion: string;
  description?: string;
  selectedTemplate: string;
}

export interface SystemSnapshot {
  metadata: SnapshotMetadata;
  config: AppConfig;
  state: AppState;
  globalTimerState?: {
    isRunning: boolean;
    currentTime: number;
    startTime: number | null;
    endTime: number | null;
  };
  localStorage: {
    [key: string]: any;
  };
  additionalData?: {
    documentTitle: string;
    currentURL: string;
    userAgent: string;
    screenResolution: string;
    formStates: any;
  };
}

export interface SnapshotValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

class SnapshotService {
  private readonly SNAPSHOT_VERSION = '1.0.0';
  private readonly APP_VERSION = '2025.1';
  
  /**
   * Exporta el estado completo del sistema
   */
  async exportSnapshot(description?: string): Promise<string> {
    try {
      console.log('📸 Iniciando exportación de snapshot...');

      // Obtener estado actual de la aplicación
      const config = localStorageManager.getConfig();
      const state = localStorageManager.getState();
      const selectedTemplate = localStorageManager.getSelectedTeamTemplate();

      // Log de debug para verificar que se capturan todos los datos
      console.log('🔍 Config capturado:', {
        timer: config.timer,
        bracket: { 
          title: config.bracket.title, 
          teams: config.bracket.teams?.length || 0,
          phases: config.bracket.phases?.length || 0,
          fields: config.bracket.fields?.length || 0,
          workingFields: config.bracket.workingFields?.length || 0,
          displayFields: config.bracket.displayFields?.length || 0
        },
        leaderboard: { 
          title: config.leaderboard.title, 
          teams: config.leaderboard.teams?.length || 0,
          phaseNames: config.leaderboard.phaseNames
        },
        podium: {
          title: config.podium.title,
          subtitle: config.podium.subtitle,
          teams: config.podium.teams
        },
        pizarra: {
          title: config.pizarra.title,
          subtitle: config.pizarra.subtitle,
          pizarras: config.pizarra.pizarras?.length || 0
        },
        carousel: {
          media: config.carousel.media?.length || 0
        }
      });

      // Obtener estado del timer global si existe
      let globalTimerState = null;
      try {
        const timerData = localStorage.getItem(STORAGE_KEYS.GLOBAL_TIMER);
        if (timerData) {
          globalTimerState = JSON.parse(timerData);
        }
      } catch (error) {
        console.warn('No se pudo obtener el estado del timer global:', error);
      }

      // Obtener todos los datos de localStorage relacionados con la app,
      // excluyendo claves que crearían recursión o conflictos al restaurar.
      const appLocalStorage: { [key: string]: any } = {};
      Object.keys(localStorage).forEach(key => {
        if (!key.startsWith(APP_KEY_PREFIX)) return;
        if (isExcludedFromSnapshot(key)) return;
        try {
          const value = localStorage.getItem(key);
          appLocalStorage[key] = value ? JSON.parse(value) : value;
        } catch {
          appLocalStorage[key] = localStorage.getItem(key);
        }
      });

      // Capturar configuraciones adicionales que pueden no estar en config/state
      const additionalData = {
        // Configuraciones del DOM que podrían no estar guardadas
        documentTitle: document.title,
        currentURL: window.location.href,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        // Datos adicionales de formularios si existen
        formStates: this.captureFormStates()
      };

      // Crear metadata del snapshot
      const metadata: SnapshotMetadata = {
        version: this.SNAPSHOT_VERSION,
        exportDate: new Date().toISOString(),
        exportTimestamp: Date.now(),
        appVersion: this.APP_VERSION,
        description: description || `Snapshot exportado el ${new Date().toLocaleString('es-ES')}`,
        selectedTemplate
      };

      // Crear el snapshot completo
      const snapshot: SystemSnapshot = {
        metadata,
        config,
        state,
        globalTimerState,
        localStorage: appLocalStorage,
        additionalData
      };

      const jsonString = JSON.stringify(snapshot, null, 2);
      console.log('✅ Snapshot exportado exitosamente');
      console.log('📊 Tamaño del snapshot:', (jsonString.length / 1024).toFixed(2), 'KB');
      console.log('📋 Datos incluidos:', {
        configSections: Object.keys(config).length,
        stateSections: Object.keys(state).length,
        localStorageKeys: Object.keys(appLocalStorage).length,
        hasGlobalTimer: !!globalTimerState,
        selectedTemplate
      });
      
      return jsonString;
    } catch (error) {
      console.error('❌ Error al exportar snapshot:', error);
      throw new Error(`Error al exportar snapshot: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Valida un snapshot antes de importarlo
   */
  validateSnapshot(snapshotData: string): SnapshotValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    try {
      // Intentar parsear el JSON
      const snapshot: SystemSnapshot = JSON.parse(snapshotData);

      // Validar estructura básica
      if (!snapshot.metadata) {
        errors.push('El snapshot no contiene metadatos válidos');
      }

      if (!snapshot.config) {
        errors.push('El snapshot no contiene configuración válida');
      }

      if (!snapshot.state) {
        errors.push('El snapshot no contiene estado válido');
      }

      // Validar metadatos
      if (snapshot.metadata) {
        if (!snapshot.metadata.version) {
          errors.push('El snapshot no tiene información de versión');
        }

        if (!snapshot.metadata.exportDate) {
          errors.push('El snapshot no tiene fecha de exportación');
        }

        // Advertencia sobre versiones
        if (snapshot.metadata.version !== this.SNAPSHOT_VERSION) {
          warnings.push(`El snapshot fue creado con una versión diferente (${snapshot.metadata.version} vs ${this.SNAPSHOT_VERSION})`);
        }

        if (snapshot.metadata.appVersion !== this.APP_VERSION) {
          warnings.push(`El snapshot fue creado con una versión diferente de la aplicación (${snapshot.metadata.appVersion} vs ${this.APP_VERSION})`);
        }
      }

      // Validar secciones principales del config
      if (snapshot.config) {
        const requiredSections: (keyof AppConfig)[] = ['timer', 'bracket', 'leaderboard', 'carousel', 'podium', 'pizarra'];
        for (const section of requiredSections) {
          if (!snapshot.config[section]) {
            errors.push(`El snapshot no contiene la sección de configuración '${section}'`);
          }
        }

        // Validar que las secciones importantes tengan los campos requeridos
        if (snapshot.config.timer && !snapshot.config.timer.title) {
          warnings.push('La configuración del timer no tiene título');
        }
        
        if (snapshot.config.bracket && !snapshot.config.bracket.title) {
          warnings.push('La configuración del bracket no tiene título');
        }
        
        if (snapshot.config.leaderboard && !snapshot.config.leaderboard.title) {
          warnings.push('La configuración de la clasificación no tiene título');
        }
        
        if (snapshot.config.podium && (!snapshot.config.podium.title || !snapshot.config.podium.subtitle)) {
          warnings.push('La configuración del podium está incompleta (faltan título o subtítulo)');
        }
        
        if (snapshot.config.pizarra && (!snapshot.config.pizarra.title || !snapshot.config.pizarra.subtitle)) {
          warnings.push('La configuración de la pizarra está incompleta (faltan título o subtítulo)');
        }
      }

      // Validar secciones principales del state
      if (snapshot.state) {
        const requiredStateSections: (keyof AppState)[] = ['timer', 'bracket', 'leaderboard', 'carousel'];
        for (const section of requiredStateSections) {
          if (!snapshot.state[section]) {
            warnings.push(`El snapshot no contiene la sección de estado '${section}'`);
          }
        }
      }

      // Validar datos adicionales si existen
      if (snapshot.additionalData) {
        if (!snapshot.additionalData.formStates) {
          warnings.push('Los datos adicionales no contienen estados de formulario');
        }
      }

    } catch (error) {
      errors.push(`Error al parsear el archivo: ${error instanceof Error ? error.message : String(error)}`);
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Importa un snapshot y restaura el estado del sistema
   */
  async importSnapshot(snapshotData: string): Promise<void> {
    try {
      console.log('📥 Iniciando importación de snapshot...');

      // Validar el snapshot
      const validation = this.validateSnapshot(snapshotData);
      if (!validation.isValid) {
        throw new Error(`Snapshot inválido: ${validation.errors.join(', ')}`);
      }

      // Mostrar advertencias si las hay
      if (validation.warnings.length > 0) {
        console.warn('⚠️ Advertencias al importar snapshot:', validation.warnings);
      }

      const snapshot: SystemSnapshot = JSON.parse(snapshotData);

      // Crear backup del estado actual antes de importar
      await this.createBackup();

      // Restaurar configuración
      if (snapshot.config) {
        localStorageManager.setConfig(snapshot.config);
        console.log('✅ Configuración restaurada');
      }

      // Restaurar estado
      if (snapshot.state) {
        localStorageManager.setState(snapshot.state);
        console.log('✅ Estado restaurado');
      }

      // Restaurar plantilla seleccionada
      if (snapshot.metadata.selectedTemplate) {
        localStorageManager.setSelectedTeamTemplate(snapshot.metadata.selectedTemplate);
        console.log('✅ Plantilla de equipos restaurada:', snapshot.metadata.selectedTemplate);
      }

      // Restaurar datos específicos de localStorage.
      // Validamos el prefijo y excluimos claves peligrosas para no inyectar
      // entradas arbitrarias ni romper la elección de maestro del timer.
      if (snapshot.localStorage) {
        const restoreErrors: string[] = [];
        Object.keys(snapshot.localStorage).forEach(key => {
          if (!key.startsWith(APP_KEY_PREFIX)) return;
          if (isExcludedFromSnapshot(key)) return;
          try {
            const value = snapshot.localStorage[key];
            localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
          } catch (error) {
            restoreErrors.push(key);
            console.warn(`No se pudo restaurar la clave '${key}':`, error);
          }
        });
        if (restoreErrors.length > 0) {
          throw new Error(
            `No se pudieron restaurar ${restoreErrors.length} claves (probable cuota excedida). ` +
            `Libera espacio o borra medios pesados y vuelve a intentarlo.`
          );
        }
      }

      // Restaurar estado del timer global si existe
      if (snapshot.globalTimerState) {
        try {
          localStorage.setItem(STORAGE_KEYS.GLOBAL_TIMER, JSON.stringify(snapshot.globalTimerState));
        } catch (error) {
          console.warn('No se pudo restaurar el estado del timer global:', error);
        }
      }

      // Restaurar datos adicionales si existen
      if (snapshot.additionalData) {
        this.restoreAdditionalData(snapshot.additionalData);
      }

      console.log('✅ Snapshot importado exitosamente');
      console.log('📊 Snapshot info:', {
        version: snapshot.metadata.version,
        exportDate: snapshot.metadata.exportDate,
        description: snapshot.metadata.description,
        configSections: Object.keys(snapshot.config).length,
        stateSections: Object.keys(snapshot.state).length,
        hasAdditionalData: !!snapshot.additionalData
      });

    } catch (error) {
      console.error('❌ Error al importar snapshot:', error);
      throw new Error(`Error al importar snapshot: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Crea un backup del estado actual antes de importar.
   * Si la cuota de localStorage está llena, aborta la importación para no dejar
   * al usuario sin un rollback posible.
   */
  private async createBackup(): Promise<void> {
    // Limpiar primero para liberar espacio
    this.cleanOldBackups();
    const backupSnapshot = await this.exportSnapshot('Backup automático antes de importar');
    const backupKey = `${STORAGE_KEYS.BACKUP_PREFIX}${Date.now()}`;
    try {
      localStorage.setItem(backupKey, backupSnapshot);
    } catch (error) {
      throw new Error(
        'No se pudo crear el backup automático antes de importar (cuota de almacenamiento excedida). ' +
        'Elimina snapshots antiguos o medios pesados e intenta de nuevo.'
      );
    }
  }

  /**
   * Limpia backups antiguos
   */
  private cleanOldBackups(): void {
    try {
      const backupKeys = Object.keys(localStorage)
        .filter(key => key.startsWith(STORAGE_KEYS.BACKUP_PREFIX))
        .sort((a, b) => {
          const timeA = parseInt(a.replace(STORAGE_KEYS.BACKUP_PREFIX, ''), 10);
          const timeB = parseInt(b.replace(STORAGE_KEYS.BACKUP_PREFIX, ''), 10);
          return timeB - timeA;
        });
      for (let i = 3; i < backupKeys.length; i++) {
        localStorage.removeItem(backupKeys[i]);
      }
    } catch (error) {
      console.warn('Error al limpiar backups antiguos:', error);
    }
  }

  /**
   * Obtiene lista de backups disponibles
   */
  getAvailableBackups(): Array<{ key: string; timestamp: number; date: string }> {
    try {
      return Object.keys(localStorage)
        .filter(key => key.startsWith(STORAGE_KEYS.BACKUP_PREFIX))
        .map(key => {
          const timestamp = parseInt(key.replace(STORAGE_KEYS.BACKUP_PREFIX, ''), 10);
          return {
            key,
            timestamp,
            date: new Date(timestamp).toLocaleString('es-ES')
          };
        })
        .sort((a, b) => b.timestamp - a.timestamp);
    } catch (error) {
      console.error('Error al obtener backups:', error);
      return [];
    }
  }

  /**
   * Restaura desde un backup específico
   */
  async restoreFromBackup(backupKey: string): Promise<void> {
    try {
      const backupData = localStorage.getItem(backupKey);
      if (!backupData) {
        throw new Error('Backup no encontrado');
      }
      await this.importSnapshot(backupData);
      console.log('✅ Restaurado desde backup:', backupKey);
    } catch (error) {
      console.error('❌ Error al restaurar backup:', error);
      throw error;
    }
  }

  /**
   * Descarga un snapshot como archivo JSON
   */
  downloadSnapshot(snapshotData: string, filename?: string): void {
    try {
      const blob = new Blob([snapshotData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `neurolimpiadas_snapshot_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      console.log('💾 Snapshot descargado como archivo');
    } catch (error) {
      console.error('❌ Error al descargar snapshot:', error);
      throw error;
    }
  }

  /**
   * Lee un archivo de snapshot
   */
  readSnapshotFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const result = event.target?.result as string;
          resolve(result);
        } catch (error) {
          reject(new Error(`Error al leer archivo: ${error instanceof Error ? error.message : String(error)}`));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error al leer el archivo'));
      };
      
      reader.readAsText(file);
    });
  }

  /**
   * Captura el estado de todos los formularios en la página
   */
  private captureFormStates(): any {
    const formStates: any = {};
    
    try {
      // Capturar todos los inputs, selects y textareas
      const inputs = document.querySelectorAll('input, select, textarea');
      
      inputs.forEach((element, index) => {
        const input = element as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        
        // Crear identificador único
        const id = input.id || input.name || `element_${index}`;
        
        // Capturar diferentes tipos de campos
        if (input.type === 'checkbox' || input.type === 'radio') {
          formStates[id] = {
            type: input.type,
            checked: (input as HTMLInputElement).checked,
            value: input.value
          };
        } else {
          formStates[id] = {
            type: input.type || input.tagName.toLowerCase(),
            value: input.value,
            placeholder: (input as HTMLInputElement).placeholder || '',
            className: input.className
          };
        }
      });

      console.log('📝 Estados de formulario capturados:', Object.keys(formStates).length, 'elementos');
      
    } catch (error) {
      console.warn('⚠️ Error al capturar estados de formulario:', error);
    }
    
    return formStates;
  }

  /**
   * Restaura datos adicionales del snapshot
   */
  private restoreAdditionalData(additionalData: any): void {
    try {
      console.log('🔄 Restaurando datos adicionales...');

      // Note: La restauración de formularios se hará automáticamente 
      // cuando los componentes se recarguen con la nueva configuración
      // ya que los estados están guardados en localStorage y config/state
      
      console.log('✅ Datos adicionales procesados:', {
        hasFormStates: !!additionalData.formStates,
        formElementsCount: additionalData.formStates ? Object.keys(additionalData.formStates).length : 0,
        documentTitle: additionalData.documentTitle,
        exportedFrom: additionalData.currentURL
      });

    } catch (error) {
      console.warn('⚠️ Error al restaurar datos adicionales:', error);
    }
  }
}

export const snapshotService = new SnapshotService();